import {
  Typography, Chip, Stack, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer,
} from '@mui/material';
import { DetailSection, DetailField } from './DetailSection';
import { severityChipSx, stateChipSx } from './severity';
import { CK, tableHeadRowSx, headCellSx, rowSx, bodyCellSx } from '../portalStyles';

const fmt = (t) => {
  if (!t) return '';
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
};

// The sensitive-data summary is stored as JSON ([{category, totalCount}]) in the
// MacieFinding row; the detail DTO may hand it back as a decoded array or as the
// raw JSON string, so accept either.
const parseSensitiveData = (v) => {
  if (Array.isArray(v)) return v;
  try { return JSON.parse(v || '[]'); } catch (_) { return []; }
};

// Amazon Macie finding body, laid out the AWS-console way: an Overview section
// (category / severity / type / description + timing), the affected S3 resource
// (bucket or bucket/object), and — for CLASSIFICATION findings — a sensitive-data
// breakdown table (data category + total occurrences) from classificationDetails.
export default function MacieDetailBody({ finding }) {
  if (!finding) return null;

  const category = finding.category || '';
  const sensitiveData = parseSensitiveData(finding.sensitiveData);

  return (
    <>
      <DetailSection title="Overview">
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.75 }}>
          {category && <Chip size="small" label={category} sx={stateChipSx} />}
          {finding.severity && <Chip size="small" label={finding.severity} sx={severityChipSx(finding.severity)} />}
        </Stack>
        {finding.description && (
          <Typography variant="body2" sx={{ mb: 1.5, color: CK.navy }}>
            {finding.description}
          </Typography>
        )}
        <DetailField label="Finding ID" value={finding.findingId} />
        <DetailField label="Category" value={category} />
        <DetailField label="Finding type" value={finding.type} />
        <DetailField label="Account" value={finding.accountId} />
        <DetailField label="Region" value={finding.region} />
        {finding.count != null && Number(finding.count) > 0 && (
          <DetailField label="Occurrences" value={String(finding.count)} />
        )}
        {finding.sample && <DetailField label="Sample" value="Yes — sampled finding" />}
        <DetailField label="First seen" value={fmt(finding.firstSeenAt)} />
        <DetailField label="Last seen" value={fmt(finding.lastSeenAt)} />
      </DetailSection>

      <DetailSection title="Affected S3 resource">
        <DetailField label="Resource type" value={finding.resourceType} />
        <DetailField label="Bucket / object" value={finding.resourceId || finding.resource} />
      </DetailSection>

      {sensitiveData.length > 0 && (
        <DetailSection title="Sensitive data">
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={headCellSx}>Data category</TableCell>
                  <TableCell align="right" sx={headCellSx}>Total count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sensitiveData.map((d, i) => (
                  <TableRow key={`${d.category}-${i}`} sx={rowSx(i)}>
                    <TableCell sx={{ ...bodyCellSx, wordBreak: 'break-word' }}>{d.category}</TableCell>
                    <TableCell align="right" sx={bodyCellSx}>{d.totalCount != null ? String(d.totalCount) : ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DetailSection>
      )}
    </>
  );
}
